import React from 'react';
import {
  ListItem,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoIcon from '@mui/icons-material/Info';
import { t } from '../../utils/localization/localization';
import { formatDate, getAuthorLabel } from '../../hooks/useVersionComparison';
import { logger } from '../../utils/logger';

/**
 * Component for rendering a single version list item
 */
const VersionListItem = ({
  version,
  index,
  isNewest,
  isViewing,
  canEdit,
  onView,
  onRestore,
  onShowDetails,
  renderChangeSummaryChips
}) => {
  const handleView = () => {
    logger.info('[VersionListItem] Viewing version:', version.versionNumber);
    logger.info('[VersionListItem] Calling onView callback...');
    onView(version);
  };

  const handleRestore = () => {
    logger.info('[VersionListItem] Restoring version:', version.versionNumber);
    onRestore(version);
  };

  const handleShowDetails = () => {
    try {
      logger.info('[VersionListItem] Showing details for version:', version.versionNumber);
      if (!version.comparisonData) {
        logger.warn('[VersionListItem] No comparison data available for version:', version.versionNumber);
        return;
      }
      onShowDetails(version);
    } catch (error) {
      logger.error('[VersionListItem] Error showing details:', error);
    }
  };

  return (
    <ListItem
      sx={{
        '&:hover': {
          backgroundColor: 'action.hover'
        },
        backgroundColor: isViewing ? 'action.selected' : 'transparent'
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1">
            {t('versionHistory.version')} {version.versionNumber || index + 1}
          </Typography>
          {version.author && (
            <Chip
              label={getAuthorLabel(version.author)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {formatDate(version.timestamp || version.version)}
        </Typography>
        {version.comparisonSummary && version.comparisonSummary !== 'No previous version to compare' && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {renderChangeSummaryChips(version.comparisonSummary)}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {version.comparisonData &&
         version.comparisonSummary &&
         version.comparisonSummary !== 'No previous version to compare' && (
          <Tooltip title={t('versionHistory.viewChangeDetails')}>
            <IconButton
              size="small"
              onClick={handleShowDetails}
              color="info"
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('versionHistory.viewVersion')}>
          <IconButton
            size="small"
            onClick={handleView}
            color="primary"
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        {canEdit && !isNewest && (
          <Tooltip title={t('versionHistory.restoreVersion')}>
            <IconButton
              size="small"
              onClick={handleRestore}
              color="secondary"
            >
              <RestoreIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </ListItem>
  );
};

export default VersionListItem;
